import React from 'react';
import './StatCardAtom.scss';

export type StatCardTone = 'default' | 'danger' | 'warning' | 'success';

export type StatCardTrendDirection = 'up' | 'down' | 'flat';

export interface StatCardTrend {
  direction: StatCardTrendDirection;
  /** Already-localized label, e.g. "+12% vs previous". */
  label: string;
}

export interface StatCardAtomProps {
  /** Already-localized label, e.g. "To do". */
  label: string;
  /** Primary value. Number, string, or fallback like "—". */
  value: React.ReactNode;
  /** Already-localized caption rendered below the value. */
  caption?: React.ReactNode;
  /** Tints the value for attention. */
  tone?: StatCardTone;
  /** Adds a colored left border accent — use for actionable cards. */
  accent?: 'info' | 'danger' | 'warning' | 'success';
  /** Optional trend indicator rendered next to the caption. */
  trend?: StatCardTrend;
  /** Optional aria-label override for the value. */
  ariaLabel?: string;
  /** Extra rows rendered after the caption row, e.g. a target line or disclosure. */
  children?: React.ReactNode;
  className?: string;
}

const TREND_ICON: Record<StatCardTrendDirection, string> = {
  up: 'pi pi-arrow-up',
  down: 'pi pi-arrow-down',
  flat: 'pi pi-minus',
};

const StatCardAtom: React.FC<StatCardAtomProps> = ({
  label,
  value,
  caption,
  tone = 'default',
  accent,
  trend,
  ariaLabel,
  children,
  className = '',
}) => {
  const classes = [
    'stat-card-atom',
    `stat-card-atom--tone-${tone}`,
    accent ? `stat-card-atom--accent-${accent}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} aria-label={ariaLabel ?? label}>
      <div className="stat-card-atom__label">{label}</div>
      <div className="stat-card-atom__value">{value}</div>
      {(caption || trend) && (
        <div className="stat-card-atom__caption-row">
          {caption ? (
            <span className="stat-card-atom__caption">{caption}</span>
          ) : null}
          {trend ? (
            <span
              className={`stat-card-atom__trend stat-card-atom__trend--${trend.direction}`}
              aria-label={trend.label}
            >
              <i
                className={`${TREND_ICON[trend.direction]} stat-card-atom__trend-icon`}
                aria-hidden="true"
              />
              <span className="stat-card-atom__trend-label">{trend.label}</span>
            </span>
          ) : null}
        </div>
      )}
      {children}
    </div>
  );
};

StatCardAtom.displayName = 'StatCardAtom';

export default StatCardAtom;
